/**
 * A SHOP OPENS IN THE LANGUAGE IT IS WRITTEN IN.
 *
 * DK Store authors its catalogue in Hebrew and sells in Hebrew and English. A
 * phone set to English was handed an English, left-to-right shop wrapped around
 * Hebrew product names; a phone set to Arabic was handed Arabic, which the shop
 * does not offer at all. The storefront chose its language from the DEVICE
 * (`index.js` bootstrap, `App.js`), and nothing on a shop page ever asked the
 * shop.
 *
 * On a shop's own pages the answer is now the shop's:
 *
 *   1. the reader's OWN choice (`languageExplicit`, written only by a switcher --
 *      see `utils/uiLanguage`), when the shop sells in it;
 *   2. otherwise the shop's primary language: the first of `allowedLanguages`,
 *      the same reading the server gives "what language is this business?"
 *      (`salonDefaultLocale`, customer-locale.util.ts).
 *
 * A business that saved no languages gets no answer here (null), and the device
 * default stands exactly as before. Pages that are not a shop's -- booking,
 * stays, courses, the customer's own area, the platform pages -- are not touched.
 *
 * The device default is NOT overwritten in storage: the shop's language is the
 * shop's, and a visitor who goes on to another business's booking page gets the
 * language they got before.
 */
import { isShopSurface, shopSlugOfPath } from './shopSurface';

export const UI_LANGUAGES = ['en', 'ar', 'he'];

const SHOP_LANGUAGES_KEY = 'stylon:shopLanguages';

const codeOf = (value) => String(value || '').trim().toLowerCase().split(/[-_]/)[0];

/**
 * THE LIST EVERY BUSINESS STARTS WITH, WHICH IS NOT A CHOICE.
 *
 * Exactly the schema default: stylon-server/prisma/schema.prisma,
 * `Salon.allowedLanguages String[] @default(["he", "ar", "en"])`. Keep the two in step.
 *
 * 88 of 92 businesses in production never touched it -- Israeli shops, many of them
 * Arabic-speaking, and businesses in Jordan, Palestine, Iraq, Pakistan, Ireland. Reading its
 * first entry as "this shop is written in Hebrew" put an Arabic-speaking business's customers,
 * on Arabic phones, into Hebrew. So only a list the owner CHANGED speaks for the shop; compared
 * in order, because putting the same three in another order ("ar", "he", "en") is a choice.
 */
export const SCHEMA_DEFAULT_ALLOWED_LANGUAGES = Object.freeze(['he', 'ar', 'en']);

/** The languages an owner chose, in their order, that this storefront can show -- [] for none. */
function chosenLanguages(list) {
  const codes = [];
  (Array.isArray(list) ? list : []).forEach((raw) => {
    const code = codeOf(raw);
    if (code && !codes.includes(code)) codes.push(code);
  });
  const untouched = codes.length === SCHEMA_DEFAULT_ALLOWED_LANGUAGES.length
    && codes.every((code, i) => code === SCHEMA_DEFAULT_ALLOWED_LANGUAGES[i]);
  if (untouched) return [];
  return codes.filter((code) => UI_LANGUAGES.includes(code));
}

/**
 * The shop's chosen languages that this storefront can show, in the shop's own order: [] when
 * it saved none, or kept the default list untouched -- the device language then stands.
 */
export function shopLanguagesOf(salon) {
  return chosenLanguages(salon?.allowedLanguages);
}

/** The reader's own earlier choice, or null when the stored language is only a device default. */
export function explicitUiLanguage() {
  try {
    if (localStorage.getItem('languageExplicit') !== '1') return null;
    const code = codeOf(localStorage.getItem('language'));
    return UI_LANGUAGES.includes(code) ? code : null;
  } catch (_) {
    return null;
  }
}

/**
 * The language a shop page is shown in, or null to leave the language alone
 * (the shop saved no languages this storefront can show).
 */
export function shopUiLanguage(languages, explicit = explicitUiLanguage()) {
  const list = Array.isArray(languages) ? languages.filter((c) => UI_LANGUAGES.includes(c)) : [];
  if (!list.length) return null;
  if (explicit && list.includes(explicit)) return explicit;
  return list[0];
}

/**
 * The pages that belong to a shop: the ones a shopper browses (utils/shopSurface)
 * and the purchase itself -- the checkout and its confirmation are the same
 * shop, and the one place a language flip mid-way would hurt most.
 */
export function isShopLanguagePath(pathname, domainMode) {
  if (isShopSurface(pathname, domainMode)) return true;
  const parts = String(pathname || '').split('/').filter(Boolean);
  const rest = domainMode ? parts : parts.slice(1);
  if (!domainMode && parts.length < 2) return false;
  return rest.length === 1 && (rest[0] === 'checkout' || rest[0] === 'order-placed');
}

/** Which shop a shop path belongs to: its slug on stylon.online, its host on its own domain. */
export function shopKeyOfPath(pathname, domainMode, hostname) {
  if (domainMode) return String(hostname || '').trim().toLowerCase();
  return shopSlugOfPath(pathname);
}

function readShopLanguageCache() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SHOP_LANGUAGES_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
}

/**
 * Remember a shop's languages, so its pages open in the right language from the
 * first paint: changing language after a page has mounted rebuilds the page
 * (App.js keys its root on the language) and everything it reports on mount is
 * reported again. An empty list is remembered too -- "this shop has none" is an
 * answer, and without it every first page of such a shop would wait to ask.
 */
export function rememberShopLanguages(key, languages) {
  const k = String(key || '').trim();
  if (!k) return;
  const cache = readShopLanguageCache();
  const next = Array.isArray(languages) ? languages : [];
  if (Array.isArray(cache[k]) && JSON.stringify(cache[k]) === JSON.stringify(next)) return;
  cache[k] = next;
  try { localStorage.setItem(SHOP_LANGUAGES_KEY, JSON.stringify(cache)); } catch (_) { /* storage refused */ }
}

/** Whether this browser has an answer for the shop's languages (an empty one counts). */
export function hasRememberedShopLanguages(key) {
  const k = String(key || '').trim();
  return !!k && Array.isArray(readShopLanguageCache()[k]);
}

export function rememberedShopLanguages(key) {
  const k = String(key || '').trim();
  if (!k) return [];
  // Read through the same rule: a list remembered before the rule existed may be the default.
  return chosenLanguages(readShopLanguageCache()[k]);
}

/** What the device alone says: the stored language, then the browser's, then English. */
export function deviceUiLanguage() {
  try {
    const stored = codeOf(localStorage.getItem('language'));
    if (UI_LANGUAGES.includes(stored)) return stored;
  } catch (_) { /* storage refused */ }
  const nav = typeof navigator !== 'undefined'
    ? codeOf(navigator.language || (navigator.languages && navigator.languages[0]) || navigator.userLanguage)
    : '';
  return UI_LANGUAGES.includes(nav) ? nav : 'en';
}

/**
 * The language to boot in. On a shop page whose languages this browser has
 * already seen, the shop's; everywhere else, the device's, as before.
 */
export function initialUiLanguage({ pathname, domainMode, hostname } = {}) {
  const device = deviceUiLanguage();
  if (!isShopLanguagePath(pathname, domainMode)) return device;
  const shopLanguage = shopUiLanguage(rememberedShopLanguages(shopKeyOfPath(pathname, domainMode, hostname)));
  return shopLanguage || device;
}
